// Permission lookup for the admin SPA. The server is authoritative
// (apps/api/src/auth/permission-map.ts + PermissionGuard enforce this
// on every write); this file is a UI hint only, so a staff user never
// sees a Publish button that would 403 on click.
//
// We read the role's permission list from the /v1/me response, which
// TanStack Query caches under ['me']. useOutletPermissions picks the
// membership that matches the current route's (orgId, outletId) and
// returns its permission set; useHasPermission is the one-off boolean
// most consumers want.

import { useQueryClient } from '@tanstack/react-query';
import type { MeResponse, Permission } from '../api/types.js';

export function hasPermission(
  permissions: readonly Permission[] | undefined,
  needed: Permission,
): boolean {
  if (!permissions) return false;
  return permissions.includes(needed);
}

// Returns the permission list of the membership that matches
// (orgId, outletId). Empty when the me cache has not loaded yet;
// callers should treat that as "no permissions" and hide gated UI
// until the query resolves rather than flashing a Publish button for
// a staff user.
export function useOutletPermissions(
  orgId: string | undefined,
  outletId: string | undefined,
): readonly Permission[] {
  const qc = useQueryClient();
  const me = qc.getQueryData<MeResponse>(['me']);
  if (!me || !orgId || !outletId) return [];
  const m = me.memberships.find((x) => x.organization.id === orgId && x.outlet.id === outletId);
  return m?.permissions ?? [];
}

export function useHasPermission(
  orgId: string | undefined,
  outletId: string | undefined,
  needed: Permission,
): boolean {
  const perms = useOutletPermissions(orgId, outletId);
  return hasPermission(perms, needed);
}

// Loyalty is restaurant-scoped (ADR 0028), so the admin loyalty page
// hides owner-only controls based on the FIRST matching membership in
// this org. In Phase 1 each user has one role per org in practice, so
// the max-of-roles-in-this-org pattern is close enough — the server
// stays authoritative via PermissionGuard on every write.
export function useOrgPermissions(orgId: string | undefined): readonly Permission[] {
  const qc = useQueryClient();
  const me = qc.getQueryData<MeResponse>(['me']);
  if (!me || !orgId) return [];
  const inOrg = me.memberships.filter((m) => m.organization.id === orgId);
  if (inOrg.length === 0) return [];
  // Union of permissions across memberships in this org. A user with
  // a staff membership at one outlet AND an owner membership org-wide
  // deserves the owner set — hiding Configure would be a worse bug
  // than showing it (the server still blocks the write).
  const union = new Set<Permission>();
  for (const m of inOrg) for (const p of m.permissions) union.add(p);
  return [...union];
}
